import * as React from 'react';
import { Link } from 'react-router-dom';
import { UserMinus, UsersIcon } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { ConfirmDialog } from '@/components/app/ConfirmDialog';
import { CopyableId } from '@/components/app/CopyableId';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { humanizeError } from '@/lib/apiClient';
import {
  useAdminCreateTeam,
  useMatchingQueue,
  useRemoveFromQueue,
} from '../hooks';
import type { QueueEntry } from '../types';

export function MatchingQueuePage() {
  const queue = useMatchingQueue(100);
  const [removeTarget, setRemoveTarget] = React.useState<QueueEntry | null>(null);
  const [showCreate, setShowCreate] = React.useState(false);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Matching queue"
        description="Users waiting to be paired. Pick any two to force-create a team."
        actions={
          <Button variant="outline" onClick={() => setShowCreate(true)}>
            <UsersIcon className="mr-1 h-4 w-4" /> Admin-create team
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Queue {queue.data?.entries?.length != null ? `· ${queue.data.entries.length}` : ''}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {queue.isLoading ? (
            <LoadingState />
          ) : queue.isError ? (
            <ErrorState
              title="Queue failed to load"
              description={humanizeError(queue.error)}
              actionLabel="Retry"
              onAction={() => queue.refetch()}
            />
          ) : (queue.data?.entries?.length ?? 0) === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">
              Queue is empty.
            </p>
          ) : (
            <ul className="divide-y text-sm">
              {queue.data?.entries.map((e) => (
                <li key={e.userId} className="flex items-center justify-between px-4 py-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1">
                      <Link
                        to={`/users/${encodeURIComponent(e.userId)}`}
                        className="font-mono text-xs text-primary hover:underline"
                      >
                        {e.userId}
                      </Link>
                      <CopyableId value={e.userId} label="User ID" iconOnly />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {e.goalCategories.join(', ')} · {e.timeZone ?? '—'} · {e.language ?? '—'} ·
                      joined {e.joinedQueueAt.slice(0, 19).replace('T', ' ')}
                      {e.expiresAt && ` · expires ${e.expiresAt.slice(0, 19).replace('T', ' ')}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {e.isMatched && <Badge variant="success">Matched</Badge>}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setRemoveTarget(e)}
                      disabled={e.isMatched}
                    >
                      <UserMinus className="mr-1 h-3.5 w-3.5" /> Remove
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {removeTarget && (
        <RemoveDialog entry={removeTarget} onClose={() => setRemoveTarget(null)} />
      )}
      {showCreate && <AdminCreateDialog onClose={() => setShowCreate(false)} />}
    </div>
  );
}

function RemoveDialog({ entry, onClose }: { entry: QueueEntry; onClose: () => void }) {
  const mutation = useRemoveFromQueue();
  return (
    <ConfirmDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Remove ${entry.userId.slice(0, 8)}… from queue?`}
      description="Removes the user from the matching queue. They'll need to be re-enqueued to match."
      reasonRequired={false}
      confirmLabel="Remove"
      submitting={mutation.isPending}
      onConfirm={async () => {
        await mutation.mutateAsync({ userId: entry.userId });
        onClose();
      }}
    />
  );
}

function AdminCreateDialog({ onClose }: { onClose: () => void }) {
  const [userA, setUserA] = React.useState('');
  const [userB, setUserB] = React.useState('');
  const mutation = useAdminCreateTeam();
  const canSubmit =
    userA.trim().length > 0 && userB.trim().length > 0 && userA.trim() !== userB.trim();

  return (
    <ConfirmDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Admin-create team?"
      description="Force-pair two users into a team, bypassing the queue."
      reasonRequired={false}
      body={
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="create-user-a">User A (userId)</Label>
            <Input id="create-user-a" value={userA} onChange={(e) => setUserA(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="create-user-b">User B (userId)</Label>
            <Input id="create-user-b" value={userB} onChange={(e) => setUserB(e.target.value)} />
          </div>
        </div>
      }
      confirmLabel="Create team"
      submitting={mutation.isPending}
      onConfirm={async () => {
        if (!canSubmit) return;
        await mutation.mutateAsync({ userIdA: userA.trim(), userIdB: userB.trim() });
        onClose();
      }}
    />
  );
}
